import React, { useEffect, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import { Check, ChevronDown, MoreHorizontal, Plus, X } from 'lucide-react';
import { peecAPI } from '../api/peecAPI';
import {
  Badge,
  PageHeader,
  PageShell,
  SearchToolbar,
  SectionCard,
  TableFrame,
  Td,
  Th,
} from '../components/PeecUI';
import { Brand, BrandSuggestion, BrandsResponse } from '../types';

interface BrandsPageProps {
  workspaceId: string;
}

const BRAND_DOT: Record<Brand['color'], string> = {
  zinc: 'bg-zinc-400',
  amber: 'bg-amber-400',
  emerald: 'bg-emerald-400',
  sky: 'bg-sky-400',
  violet: 'bg-violet-400',
  rose: 'bg-rose-400',
};

const splitCsv = (value: string) => value
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const BrandsPage: React.FC<BrandsPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [brandData, setBrandData] = useState<BrandsResponse>({
    total: 0,
    page: 1,
    limit: 20,
    trial_ends_in_days: 0,
    data: [],
    suggestions: [],
  });
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [newBrand, setNewBrand] = useState({ displayName: '', trackedNames: '', domains: '' });

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const data = await peecAPI.getBrands(workspaceId);
        setBrandData(data);
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [workspaceId]);

  const handleAddBrand = async () => {
    if (!newBrand.displayName.trim()) return;

    try {
      const trackedNames = splitCsv(newBrand.trackedNames);
      const domains = splitCsv(newBrand.domains);
      const createdBrand = await peecAPI.createBrand(workspaceId, {
        display_name: newBrand.displayName.trim(),
        tracked_names: trackedNames.length > 0 ? trackedNames : [newBrand.displayName.trim()],
        domains,
      });

      setBrandData((current) => ({
        ...current,
        total: current.total + 1,
        data: [...current.data, createdBrand],
      }));
      setNewBrand({ displayName: '', trackedNames: '', domains: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Failed to add brand:', error);
    }
  };

  const handleSuggestionAction = async (
    suggestion: BrandSuggestion,
    nextStatus: BrandSuggestion['status']
  ) => {
    try {
      await peecAPI.updateBrandSuggestion(workspaceId, suggestion.id, nextStatus);

      if (nextStatus === 'accepted') {
        const createdBrand = await peecAPI.createBrand(workspaceId, {
          display_name: suggestion.name,
          tracked_names: [suggestion.name],
          domains: [suggestion.domain],
        });

        setBrandData((current) => ({
          ...current,
          total: current.total + 1,
          data: [...current.data, { ...createdBrand, mentions: suggestion.mentions }],
          suggestions: current.suggestions.filter((item) => item.id !== suggestion.id),
        }));
        return;
      }

      setBrandData((current) => ({
        ...current,
        suggestions: current.suggestions.filter((item) => item.id !== suggestion.id),
      }));
    } catch (error) {
      console.error('Failed to update suggestion:', error);
    }
  };

  const filteredBrands = brandData.data.filter((brand) => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return true;

    return [brand.display_name, ...brand.tracked_names, ...brand.domains]
      .some((value) => value.toLowerCase().includes(query));
  });

  return (
    <PageShell>
      <PageHeader title={t('pages.brandsPage')} />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-balance text-lg font-semibold tracking-tight text-zinc-950">
            {t('brands.yourBrands')} · {brandData.total}
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            追踪您的品牌和在 AI 回答中出现的竞品。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowAddForm((current) => !current)}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-zinc-950 px-3 text-sm font-medium text-white outline-none transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
          >
            <Plus size={16} aria-hidden="true" />
            {t('brands.addBrand')}
          </button>
        </div>
      </div>

      {showAddForm && (
        <SectionCard className="mb-5" title={t('brands.addBrand')}>
          <div className="grid gap-4 px-5 py-4 lg:grid-cols-3">
            <div>
              <label htmlFor="brand-display-name" className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-400">
                {t('brands.displayName')}
              </label>
              <input
                id="brand-display-name"
                name="displayName"
                type="text"
                autoComplete="organization"
                value={newBrand.displayName}
                onChange={(event) => setNewBrand((current) => ({ ...current, displayName: event.target.value }))}
                className="h-10 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-300"
              />
            </div>
            <div>
              <label htmlFor="brand-tracked-names" className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-400">
                {t('brands.trackedNames')}
              </label>
              <input
                id="brand-tracked-names"
                name="trackedNames"
                type="text"
                autoComplete="off"
                value={newBrand.trackedNames}
                onChange={(event) => setNewBrand((current) => ({ ...current, trackedNames: event.target.value }))}
                className="h-10 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-300"
                spellCheck={false}
              />
              <p className="mt-2 text-xs text-zinc-400">{t('brands.trackedNamesHelp')}</p>
            </div>
            <div>
              <label htmlFor="brand-domains" className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-zinc-400">
                {t('brands.domains')}
              </label>
              <input
                id="brand-domains"
                name="domains"
                type="url"
                inputMode="url"
                autoComplete="url"
                value={newBrand.domains}
                onChange={(event) => setNewBrand((current) => ({ ...current, domains: event.target.value }))}
                className="h-10 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-300"
                spellCheck={false}
              />
              <p className="mt-2 text-xs text-zinc-400">{t('brands.domainsHelp')}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3">
            <button
              onClick={() => setShowAddForm(false)}
              className="inline-flex h-9 items-center rounded-lg border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-600 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              {t('brands.cancel')}
            </button>
            <button
              onClick={handleAddBrand}
              className="inline-flex h-9 items-center rounded-lg bg-zinc-950 px-3 text-sm font-medium text-white outline-none transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              {t('brands.saveBrand')}
            </button>
          </div>
        </SectionCard>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px]">
        <SectionCard>
          <SearchToolbar
            placeholder={t('brands.searchPlaceholder')}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            name="brand-search"
            ariaLabel={t('brands.searchPlaceholder')}
          />
          <TableFrame>
            <thead>
              <tr>
                <Th align="left">颜色</Th>
                <Th align="left">{t('brands.displayName')}</Th>
                <Th align="left">{t('brands.trackedNames')}</Th>
                <Th align="left">{t('brands.domains')}</Th>
                <Th>{t('brands.mentions')}</Th>
                <Th>
                  <ChevronDown size={14} className="mx-auto text-zinc-300" aria-hidden="true" />
                </Th>
              </tr>
            </thead>
            <tbody>
              {filteredBrands.map((brand) => (
                <tr key={brand.id} className="hover:bg-zinc-50">
                  <Td align="left">
                    <span className={`block h-3 w-3 rounded-full ${BRAND_DOT[brand.color]}`} aria-hidden="true" />
                  </Td>
                  <Td align="left">
                    <div className="flex items-center gap-2">
                      <img
                        className="h-5 w-5 rounded-md bg-white"
                        src={`https://www.google.com/s2/favicons?domain=${brand.domains[0] ?? 'example.com'}&sz=64`}
                        alt=""
                        width={20}
                        height={20}
                        loading="lazy"
                      />
                      <span className="font-medium text-zinc-950">{brand.display_name}</span>
                      {brand.is_own ? <Badge tone="blue">{t('brands.you')}</Badge> : null}
                    </div>
                  </Td>
                  <Td align="left" className="text-zinc-700">{brand.tracked_names.join(', ')}</Td>
                  <Td align="left" className="text-zinc-700">{brand.domains.join(', ')}</Td>
                  <Td className="tabular-nums font-medium text-zinc-950">{brand.mentions}</Td>
                  <Td>
                    <button
                      aria-label={`More actions for ${brand.display_name}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 outline-none transition-colors hover:bg-zinc-100 hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      <MoreHorizontal size={14} aria-hidden="true" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
          {filteredBrands.length === 0 && !loading ? (
            <div className="px-5 py-10 text-center text-sm text-zinc-500">{t('brands.noBrands')}</div>
          ) : null}
        </SectionCard>

        <SectionCard title={`${t('brands.brandSuggestions')} · ${brandData.suggestions.length}`}>
          <div className="divide-y divide-black/[0.05]">
            {brandData.suggestions.map((suggestion) => (
              <article key={suggestion.id} className="px-4 py-3">
                <div className="text-xs font-medium tabular-nums text-zinc-400">
                  {suggestion.mentions} {t('brands.mentions').toLowerCase()}
                </div>
                <div className="mt-1 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <img
                        className="h-5 w-5 rounded-md bg-white"
                        src={`https://www.google.com/s2/favicons?domain=${suggestion.domain}&sz=64`}
                        alt=""
                        width={20}
                        height={20}
                        loading="lazy"
                      />
                      <span className="truncate font-medium text-zinc-950">{suggestion.name}</span>
                    </div>
                    <div className="mt-1 truncate text-sm text-zinc-500">{suggestion.domain}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      aria-label={`${t('brands.dismissSuggestion')}: ${suggestion.name}`}
                      onClick={() => handleSuggestionAction(suggestion, 'dismissed')}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-50 text-zinc-400 outline-none transition-colors hover:bg-zinc-100 hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                    <button
                      aria-label={`${t('brands.addSuggestion')}: ${suggestion.name}`}
                      onClick={() => handleSuggestionAction(suggestion, 'accepted')}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-50 text-zinc-400 outline-none transition-colors hover:bg-zinc-100 hover:text-emerald-600 focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      <Check size={14} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {brandData.suggestions.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">{t('brands.noSuggestions')}</div>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </PageShell>
  );
};

export default BrandsPage;
